/**
 * Price-tier proxy for compare pairs.
 *
 * EPA and vPIC ship no MSRP, so there is no price column to gate on. Brand
 * tier is the next-best proxy and it is what actually decides whether two
 * cars are cross-shopped: MPG ratio plus EPA class let "Mazda3 vs Porsche
 * 911" through because they happen to share a class bucket.
 */

const EXOTIC = new Set([
  "porsche",
  "ferrari",
  "lamborghini",
  "mclaren",
  "bentley",
  "rolls-royce",
  "aston-martin",
  "bugatti",
  "lotus",
  "maybach",
  "maserati",
]);

const PREMIUM = new Set([
  "bmw",
  "mercedes-benz",
  "audi",
  "lexus",
  "jaguar",
  "land-rover",
  "volvo",
  "acura",
  "infiniti",
  "genesis",
  "cadillac",
  "lincoln",
  "alfa-romeo",
  "mini",
  "tesla",
  "polestar",
  "rivian",
  "lucid",
]);

/** 0 = mainstream, 1 = premium, 2 = exotic / ultra-luxury. */
export function makeTier(makeSlug: string): 0 | 1 | 2 {
  if (EXOTIC.has(makeSlug)) return 2;
  if (PREMIUM.has(makeSlug)) return 1;
  return 0;
}

/**
 * Flagship / halo models where even a one-tier gap is nonsense. Nobody
 * cross-shops an S-Class against a Mazda3, so these require an equal tier on
 * the other side.
 */
const FLAGSHIP_MODELS = new Set([
  "mercedes-benz/s-class",
  "mercedes-benz/e-class",
  "mercedes-benz/maybach",
  "mercedes-benz/g-class",
  "bmw/7-series",
  "bmw/8-series",
  "audi/a8",
  "audi/a7",
  "lexus/ls",
  "lexus/lc",
  "porsche/panamera",
  "porsche/911",
  "porsche/taycan",
  "jaguar/f-type",
  "land-rover/range-rover",
  "cadillac/escalade",
  "lincoln/navigator",
]);

export function isFlagshipModel(makeSlug: string, modelSlug: string): boolean {
  return FLAGSHIP_MODELS.has(`${makeSlug}/${modelSlug}`);
}

/** Are two nameplates in comparable price territory? */
export function comparableTier(
  a: { makeSlug: string; modelSlug: string },
  b: { makeSlug: string; modelSlug: string },
): boolean {
  const ta = makeTier(a.makeSlug);
  const tb = makeTier(b.makeSlug);
  if (Math.abs(ta - tb) >= 2) return false;
  if (ta !== tb && (isFlagshipModel(a.makeSlug, a.modelSlug) || isFlagshipModel(b.makeSlug, b.modelSlug)))
    return false;
  return true;
}
